import { AdobeAnalyticsUtil } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/AdobeAnalyticsUtil.js';
import { CarData, CloseType, FitAssistantEventObject, FitAssistantEventData } from '../Types/Tracking.ts';
import { CarInformation } from '../Types/FitAssistantData.ts';
import { Constants } from '../Constants.ts';
import FitAssistantStep from '../Enums/FitAssistantStep.ts';
import FitAssistantVariation from '../Enums/FitAssistantVariation.ts';

export const trackChooseRoofRack = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  fitAssistantStep: FitAssistantStep,
  carInformation: CarInformation[],
  selectedRoofRack: string
) => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation, fitAssistantStep, true);
  eventObject.fit_assistant.fatype = 'Find RSS for RTT';
  eventObject.fit_assistant.selectedRRS = selectedRoofRack;
  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CHOOSE_ROOF_RACK, eventObject);
};

export const trackFindRoofRack = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  fitAssistantStep: FitAssistantStep,
  carInformation: CarInformation[]
): void => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation, fitAssistantStep, true);
  eventObject.fit_assistant.fatype = 'Find RSS for RTT';
  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.FIND_ROOF_RACK, eventObject);
};

export const trackOpen = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  fitAssistantStep: FitAssistantStep,
  carInformation: CarInformation[]
): void => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation, fitAssistantStep, true);
  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.OPEN_WITH_SELECTION, eventObject);
};

export const trackOpenEmpty = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  carInformation: CarInformation[],
  fitAssistantStep: FitAssistantStep
): void => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation, fitAssistantStep, true);

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.OPEN_WITHOUT_SELECTION, eventObject);
};

export const trackClose = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep,
  isYearFirst: boolean,
  carInformation: CarInformation[],
  closeType: CloseType,
  clickLink?: string
): void => {
  let eventObject = getEventObject(
    fitAssistantVariation,
    isYearFirst,
    carInformation,
    fitAssistantStep,
    true,
    closeType
  );

  if (clickLink) {
    eventObject.fit_assistant.clicklink = clickLink;
  } else {
    delete eventObject.fit_assistant.clicklink;
  }

  if (fitAssistantStep === FitAssistantStep.RoofRacks && fitAssistantVariation === FitAssistantVariation.RooftopTents) {
    eventObject.fit_assistant.fatype = 'Find RSS for RTT';
  }

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.EXIT, eventObject);
};

export const trackNoFit = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep,
  isYearFirst: boolean,
  carInformation: CarInformation[],
  closeType?: CloseType,
  clickLink?: string
): void => {
  let eventObject = getEventObject(
    fitAssistantVariation,
    isYearFirst,
    carInformation,
    fitAssistantStep,
    true,
    closeType
  );

  if (clickLink) {
    eventObject.fit_assistant.clicklink = clickLink;
  } else {
    delete eventObject.fit_assistant.clicklink;
  }

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CLOSE_NOFIT, eventObject);
};

export const trackFit = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep,
  isYearFirst: boolean,
  carInformation: CarInformation[],
  closeType?: CloseType,
  clickLink?: string
): void => {
  let eventObject = getEventObject(
    fitAssistantVariation,
    isYearFirst,
    carInformation,
    fitAssistantStep,
    true,
    closeType
  );

  if (clickLink) {
    eventObject.fit_assistant.clicklink = clickLink;
  } else {
    delete eventObject.fit_assistant.clicklink;
  }

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CLOSE_CONFIRMATION, eventObject);
};

export const trackUnconfirmed = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep,
  isYearFirst: boolean,
  carInformation: CarInformation[],
  closeType?: CloseType,
  clickLink?: string
): void => {
  let eventObject = getEventObject(
    fitAssistantVariation,
    isYearFirst,
    carInformation,
    fitAssistantStep,
    true,
    closeType
  );

  if (clickLink) {
    eventObject.fit_assistant.clicklink = clickLink;
  } else {
    delete eventObject.fit_assistant.clicklink;
  }

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CLOSE_UNCONFIRMED, eventObject);
};

export const trackFitNoRacks = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep,
  isYearFirst: boolean,
  carInformation: CarInformation[],
  closeType?: CloseType,
  clickLink?: string
): void => {
  let eventObject = getEventObject(
    fitAssistantVariation,
    isYearFirst,
    carInformation,
    fitAssistantStep,
    true,
    closeType
  );

  if (clickLink) {
    eventObject.fit_assistant.clicklink = clickLink;
  } else {
    delete eventObject.fit_assistant.clicklink;
  }

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CLOSE_CONFIRMED_NO_RACKS, eventObject);
};

export const trackView = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep,
  isYearFirst: boolean,
  carInformation: CarInformation[],
  hasLoadedFromCookie: boolean
): void => {
  if (!hasLoadedFromCookie) return;
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation, fitAssistantStep, true);
  eventObject.fit_assistant.selectedfilter = '';

  if (fitAssistantStep === FitAssistantStep.RoofRacks && fitAssistantVariation === FitAssistantVariation.RooftopTents) {
    eventObject.fit_assistant.fatype = 'Find RSS for RTT';
  }

  let carVariation1 = getMountingCarVariation1(fitAssistantVariation, fitAssistantStep);

  if (carVariation1) {
    eventObject.fit_assistant.carvariation1 = carVariation1;
  }

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.VIEW, eventObject);
};

export const trackChooseVariation = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  carInformation: CarInformation[]
): void => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation);

  eventObject.fit_assistant.selectedfilter = '';

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CHOOSE_VARIATION, eventObject);
};

export const trackChooseYear = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  carInformation: CarInformation[]
): void => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation);

  eventObject.fit_assistant.title = 'Year';
  eventObject.fit_assistant.selectedfilter = '';

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CHOOSE_YEAR, eventObject);
};

// Analytics tracks the trunk bike rack model choice under the carvariation rule (ADO #116). Other FAs
// have a real variation step that already sends that event.
export const trackChooseModel = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  carInformation: CarInformation[]
): void => {
  if (fitAssistantVariation !== FitAssistantVariation.TruckAndSpareTireBikeRacks) return;

  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation);

  eventObject.fit_assistant.title = 'Model';
  eventObject.fit_assistant.selectedfilter = '';

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CHOOSE_VARIATION, eventObject);
};

export const trackChooseVariation1 = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  carInformation: CarInformation[]
): void => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation);

  eventObject.fit_assistant.title = 'Variation1';
  eventObject.fit_assistant.selectedfilter = '';

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.CHOOSE_VARIATION_1, eventObject);
};

export const trackOK = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep,
  isYearFirst: boolean,
  carInformation: CarInformation[]
): void => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation, fitAssistantStep, true);

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.OK, eventObject);
};

export const trackReset = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep,
  isYearFirst: boolean,
  carInformation: CarInformation[]
): void => {
  let eventObject = getEventObject(fitAssistantVariation, isYearFirst, carInformation, fitAssistantStep, true);

  AdobeAnalyticsUtil.DataLayer.TrackEvent(Constants.Tracking.RESET, eventObject);
};

export const TrackAddToCart = () => {
  const adobeData = window.adobeDataLayer?.products?.[0];
  const analyticsDataset = document.querySelector<HTMLElement>('.product-detail__buy-area .analyticsData')?.dataset;
  const productDetail = window._THULEDATA?.ProductDetail;
  const gaDiscountPrice = document.querySelector('.ga-discount-price')?.textContent?.trim() ?? '';
  const gaPrice = document.querySelector('.ga-price')?.textContent?.trim() ?? '';

  let product = {
    id: adobeData?.id || analyticsDataset?.productid || productDetail?.ProductId || '',
    name: adobeData?.name || analyticsDataset?.thuleproductname || productDetail?.WebTitle1 || '',
    price: gaDiscountPrice || gaPrice,
    colour: adobeData?.colour || analyticsDataset?.colorspecification || '',
    original_price: gaPrice,
    category: adobeData?.category || analyticsDataset?.category || '',
    subcategory: adobeData?.subcategory || analyticsDataset?.subcategory || '',
    over_category: adobeData?.over_category || analyticsDataset?.overcategory || '',
    quantity: '1',
  };

  let analyticsData = {
    add_to_cart_variant: 'add_to_cart_NoFA',
    products: [product],
  };

  AdobeAnalyticsUtil.DataLayer.TrackEvent('cart_addition', analyticsData);
};

export const TrackRackMounting = (nextStep: string) => {
  let carVariation1 =
    nextStep === FitAssistantStep.RackMounting
      ? Constants.Tracking.CAR_VARIATION_1.RACK_MOUNTED
      : Constants.Tracking.CAR_VARIATION_1.DIRECT_ON_VEHICLE;

  let fitAssistant: FitAssistantEventData = {
    title: 'Variation1',
    fatype: getFAType(undefined, []),
    pagetype: window.adobeDataLayer ? window.adobeDataLayer.page.type : '',
    ordervariation: 'ybmr',
    brand: '',
    model: '',
    year: '',
    carvariation: '',
    carvariation1: carVariation1,
    selectedfilter: '',
  };

  let eventObject: FitAssistantEventObject = { fit_assistant: fitAssistant };

  AdobeAnalyticsUtil.DataLayer.TrackEvent('fit_assistant_choose_carvariation1', eventObject);
};

const mountingCarVariation1ByStep: Partial<Record<FitAssistantStep, string>> = {
  [FitAssistantStep.RackMounting]: Constants.Tracking.CAR_VARIATION_1.RACK_MOUNTED,
  [FitAssistantStep.Year]: Constants.Tracking.CAR_VARIATION_1.DIRECT_ON_VEHICLE,
};

const getMountingCarVariation1 = (
  fitAssistantVariation: FitAssistantVariation,
  fitAssistantStep: FitAssistantStep
): string | undefined =>
  fitAssistantVariation === FitAssistantVariation.RoofPlatforms
    ? mountingCarVariation1ByStep[fitAssistantStep]
    : undefined;

const getEventObject = (
  fitAssistantVariation: FitAssistantVariation,
  isYearFirst: boolean,
  carInformation?: CarInformation[],
  fitAssistantStep?: FitAssistantStep,
  includeHeadline?: boolean,
  closeType?: CloseType
): FitAssistantEventObject => {
  let { brand, model, year, carVariation, carVariation1, selectedRRS } = getCarData(carInformation);

  let fitAssistant: FitAssistantEventData = {
    fatype: getFAType(fitAssistantVariation, carInformation ?? []),
    pagetype: window.adobeDataLayer ? window.adobeDataLayer.page.type : '',
    ordervariation: isYearFirst ? 'ybmr' : 'bmyr',
    brand: brand ?? '',
    model: model ?? '',
    year: year ?? '',
    carvariation: carVariation ?? '',
    carvariation1: carVariation1 ?? '',
    closetype: closeType ?? undefined,
    selectedfilter: getSelectedFilter(brand, model, year, carVariation, carVariation1, fitAssistantVariation),
  };

  if (!closeType) {
    delete fitAssistant.closetype;
  }

  if (selectedRRS) {
    fitAssistant.selectedRRS = selectedRRS;
  } else {
    delete fitAssistant.selectedRRS;
  }

  if (includeHeadline) {
    fitAssistant.title = getFitAssistantStepName(fitAssistantStep);
  } else {
    delete fitAssistant.title;
  }

  let eventObject: FitAssistantEventObject = { fit_assistant: fitAssistant };

  return eventObject;
};

const getFitAssistantStepName = (fitAssistantStep: FitAssistantStep | undefined): string => {
  // Generic name because other FAs will have a second variation step that is not a van size.
  if (fitAssistantStep === FitAssistantStep.VanSize) return 'Variation1';

  let currentKeyByValue = Object.entries(FitAssistantStep).find((entry) => entry[1] === fitAssistantStep);

  if (!currentKeyByValue) return '';

  return currentKeyByValue[0].match(/[A-Z][a-z]+/g)?.join(' ') ?? '';
};

const getCarData = (carInformation: CarInformation[] | undefined): CarData => {
  if (!carInformation) {
    return {
      brand: '',
      model: '',
      year: '',
      carVariation: '',
    };
  }

  const textForStep = (step: FitAssistantStep) =>
    carInformation.find((x: CarInformation) => x.parameterName === step)?.text;

  let carData: CarData = {
    brand: textForStep(FitAssistantStep.Make),
    model: textForStep(FitAssistantStep.Model),
    year: textForStep(FitAssistantStep.Year),
    carVariation: textForStep(FitAssistantStep.Variation),
    carVariation1: textForStep(FitAssistantStep.VanSize),
  };

  if (carInformation.find((x: CarInformation) => x.parameterName === FitAssistantStep.RoofRacks))
    carData.selectedRRS = carInformation.find(
      (x: CarInformation) => x.parameterName === FitAssistantStep.RoofRacks
    )?.text;

  return carData;
};

const getSelectedFilter = (
  brand: string,
  model: string,
  year: string,
  carVariation: string,
  carVariation1: string | undefined,
  fitAssistantVariation: FitAssistantVariation
) => {
  let shortVersions = [
    FitAssistantVariation.CarSeats,
    FitAssistantVariation.DogCrates,
    FitAssistantVariation.TruckBedBikeRacks,
    FitAssistantVariation.TruckAndSpareTireBikeRacks,
  ];

  if ((shortVersions.includes(fitAssistantVariation) && model && year) || carVariation) {
    return [brand, model, year, carVariation, carVariation1].filter(Boolean).join(' ');
  }

  return '';
};

const getFAType = (
  fitAssistantVariation: FitAssistantVariation | undefined,
  carInformation: CarInformation[]
): string => {
  if (
    fitAssistantVariation === FitAssistantVariation.RooftopTents &&
    carInformation.find((x: CarInformation) => x.parameterName === FitAssistantStep.RoofRacks)
  )
    return 'Find RSS for RTT';

  return window.dataLayer[0]?.gaAggregate ?? '';
};
